/**
 * FAQ Page
 * ========
 * Frequently asked questions with accordion-style answers.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, MessageCircle, ArrowRight } from 'lucide-react';
import { Button } from '../../components/shared/Button';
import { SectionHeader } from '../../components/shared/SectionBadge';
import { FadeReveal, StaggerReveal } from '../../components/animations/FadeReveal';
import { FAQ_ITEMS } from '../../utils/constants';

/**
 * FAQ Page Component
 * Displays frequently asked questions.
 * 
 * @returns {JSX.Element} FAQ page
 */
function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  
  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-dme-blue via-dme-blue-600 to-dme-blue-800 py-24 lg:py-32">
        <div className="container-dme relative z-10">
          <FadeReveal>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6">
                <MessageCircle className="w-4 h-4 text-dme-orange" />
                Get Answers
              </span>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                Frequently Asked Questions
              </h1>
              <p className="text-lg lg:text-xl text-white/80 max-w-2xl mx-auto">
                Find answers to common questions about DME-CPH.
              </p>
            </div>
          </FadeReveal>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="section bg-white">
        <div className="container-dme">
          <div className="max-w-3xl mx-auto">
            <StaggerReveal selector=".faq-item" stagger={0.05}>
              <div className="space-y-4">
                {FAQ_ITEMS.map((item, index) => (
                  <div key={index} className="faq-item border border-dme-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleQuestion(index)}
                      className="w-full flex items-center justify-between p-6 text-left hover:bg-dme-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-dme-blue-800 pr-4">
                        {item.question}
                      </span>
                      <ChevronDown 
                        className={`w-5 h-5 text-dme-gray-400 flex-shrink-0 transition-transform ${
                          openIndex === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${
                        openIndex === index ? 'max-h-96' : 'max-h-0'
                      }`}
                    >
                      <div className="px-6 pb-6 text-dme-gray-600 leading-relaxed">
                        {item.answer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </StaggerReveal>
          </div>
        </div>
      </section>
      
      {/* Contact CTA */}
      <section className="section bg-dme-blue-50">
        <div className="container-dme">
          <FadeReveal>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading font-bold text-3xl lg:text-4xl text-dme-blue-800 mb-4">
                Still Have Questions?
              </h2>
              <p className="text-lg text-dme-gray-600 mb-8">
                Our team is here to help. Reach out and we'll get back to you within 24 hours.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button variant="primary" size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                    Contact Us
                  </Button>
                </Link>
                <a href={`mailto:info@dmecph.com`}>
                  <Button variant="secondary" size="lg">
                    Email Support
                  </Button>
                </a>
              </div>
            </div>
          </FadeReveal>
        </div>
      </section>
    </div>
  );
}

export default FAQ;
